import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Grid, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  addCorporatePassportDocument,
  editCorporatePassportDocument,
} from '../../../../api/endpoints/corporateEmployee';
import type { CorporatePassportDocument } from '../../../../api/types/corporateEmployee';
import { FormDialog } from '../../../../components/common';

const schema = z.object({
  passportCountry: z.string().min(1, 'Ülke gerekli'),
  passportNumber: z.string().min(1, 'Pasaport numarası gerekli'),
  issueDate: z.string().min(1, 'Veriliş tarihi gerekli'),
  expirationDate: z.string().min(1, 'Son geçerlilik tarihi gerekli'),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = { passportCountry: '', passportNumber: '', issueDate: '', expirationDate: '' };

function toDateInputValue(iso?: string): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

/** Add/edit dialog for a Pasaport Belgesi sub-record. NOTE: the real API's
 *  EditCorporatePassportDocument behaves as delete-old+insert-new — the record comes back
 *  under a new `passportID` after saving, which is fine here since the parent list is
 *  refetched on success. */
export function PassportFormDialog({
  open,
  employeeID,
  passport,
  onClose,
}: {
  open: boolean;
  employeeID: number;
  /** null = create mode; otherwise edit mode for this row. */
  passport: CorporatePassportDocument | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!passport;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  useEffect(() => {
    if (open) {
      reset(
        passport
          ? {
              passportCountry: passport.passportCountry ?? '',
              passportNumber: passport.passportNumber ?? '',
              issueDate: toDateInputValue(passport.issueDate),
              expirationDate: toDateInputValue(passport.expirationDate),
            }
          : emptyValues
      );
    }
  }, [open, passport, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = {
        ...values,
        issueDate: `${values.issueDate}T00:00:00`,
        expirationDate: `${values.expirationDate}T00:00:00`,
      };
      return isEdit && passport
        ? editCorporatePassportDocument({ ...passport, ...payload })
        : addCorporatePassportDocument({ employeeID, ...payload });
    },
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Pasaport belgesi güncellendi.' : 'Pasaport belgesi eklendi.', {
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['corporateEmployee', 'passports', employeeID] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Pasaport Belgesini Güncelle' : 'Yeni Pasaport Belgesi'}
      onClose={onClose}
      onSubmit={submit}
      submitting={mutation.isPending}
      error={mutation.error}
      maxWidth="xs"
    >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Ülke"
                error={!!errors.passportCountry}
                helperText={errors.passportCountry?.message}
                {...register('passportCountry')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Pasaport Numarası"
                error={!!errors.passportNumber}
                helperText={errors.passportNumber?.message}
                {...register('passportNumber')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Veriliş Tarihi"
                slotProps={{ inputLabel: { shrink: true } }}
                error={!!errors.issueDate}
                helperText={errors.issueDate?.message}
                {...register('issueDate')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Son Geçerlilik Tarihi"
                slotProps={{ inputLabel: { shrink: true } }}
                error={!!errors.expirationDate}
                helperText={errors.expirationDate?.message}
                {...register('expirationDate')}
              />
            </Grid>
          </Grid>
    </FormDialog>
  );
}
